'use client';

import { FC, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState, MouseEvent } from 'react';

import classNames from 'classnames';
import { FocusTrap } from 'focus-trap-react';
import { CSSTransition } from 'react-transition-group';

import { useBodyOverflowHidden } from '../../../../hooks/useBodyOverflowHidden/useBodyOverflowHidden';
import useId from '../../../../hooks/useId/useId';
import { Backdrop } from '../../../backdrop/components/Backdrop/Backdrop';
import { IconButton } from '../../../button/components/IconButton/IconButton';
import { ClientPortal } from '../../../client-portal/components/ClientPortal/ClientPortal';
import { Icon } from '../../../icon/components/Icon/Icon';
import { Typography } from '../../../typography/components/Typography/Typography';

import styles from './Dialog.module.css';

type Overflow = 'visible' | 'hidden' | 'clip' | 'scroll' | 'auto' | 'initial' | 'inherit';

interface Props {
   open?: boolean;
   title?: string;
   icon?: string;
   closeOnOutsideClick?: boolean;
   closable?: boolean;
   onDialogClose?: () => void;
   dialogBodyOverflowHidden?: boolean;
   contentOverflowX?: Overflow;
   contentOverflowY?: Overflow;
   disableBackdrop?: boolean;
   disableEnforceFocus?: boolean;
   width?: string;
   children: ReactNode | Array<ReactNode>;
   actions?: ReactNode | Array<ReactNode>;
   actionsLayout?: 'auto' | 'vertical';
}

const transitionClassNames = {
   enter: styles['dialog-enter'],
   enterActive: styles['dialog-enter-active'],
   exit: styles['dialog-exit'],
   exitActive: styles['dialog-exit-active']
};

export const Dialog: FC<Props> = ({ open = false, title, icon, dialogBodyOverflowHidden = true, closeOnOutsideClick = true, closable = true, onDialogClose, contentOverflowX = 'hidden', contentOverflowY = 'auto', disableBackdrop = false, disableEnforceFocus = false, width, children, actions, actionsLayout = 'auto' }) => {
   const titleId = useId();

   const nodeRef = useRef(null);

   useBodyOverflowHidden(open);

   const [dialogOpen, setDialogOpen] = useState(open);
   const [focusTrapActive, setFocusTrapActive] = useState(false);
   const [trigger, setTrigger] = useState<HTMLElement | null>(null);

   const handleClose = useCallback(() => {
      onDialogClose?.();
   }, [onDialogClose]);

   const handleContainerClick = (event: MouseEvent) => {
      if (event.target === event.currentTarget && closeOnOutsideClick) {
         handleClose();
      }
   };

   useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
         if (event.key === 'Escape') {
            handleClose();
         }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
         document.removeEventListener('keydown', handleKeyDown);
      };
   }, [handleClose]);

   useLayoutEffect(() => {
      if (open) {
         setTrigger(document.activeElement as HTMLElement | null);
         setDialogOpen(true);
      } else {
         setDialogOpen(false);
      }
   }, [open]);

   return (
      <ClientPortal selector="body">
         {!disableBackdrop && <Backdrop type="dialog" closeOnOutsideClick={closeOnOutsideClick} isOpen={open} onClose={handleClose} />}

         <CSSTransition
            nodeRef={nodeRef}
            in={dialogOpen}
            timeout={200}
            classNames={transitionClassNames}
            mountOnEnter
            unmountOnExit
            onEnter={(): void => {
               setFocusTrapActive(true);
            }}
            onExited={(): void => {
               setFocusTrapActive(false);
               setTrigger(null);
            }}
         >
            <FocusTrap
               active={!disableEnforceFocus && focusTrapActive}
               focusTrapOptions={{
                  allowOutsideClick: true,
                  tabbableOptions: { displayCheck: 'none' },
                  setReturnFocus: () => trigger ?? /* istanbul ignore next */ false
               }}
            >
               <div role="presentation" className="m-component-feedback-dialog-container-spacing-margin phone:m-0 fixed inset-0 z-[9999] flex items-center justify-center" onClick={handleContainerClick}>
                  <div
                     role="dialog"
                     aria-modal="true"
                     aria-labelledby={titleId}
                     style={{ width }}
                     className="shadow-component-feedback-dialog-container-box-shadow p-component-feedback-dialog-container-spacing-padding gap-component-feedback-dialog-container-spacing-gap min-w-component-feedback-dialog-container-sizing-min-width bg-component-feedback-dialog-container-color-background rounded-component-feedback-dialog-container-border-radius phone:rounded-none phone:max-h-none phone:h-full relative flex max-h-[calc(100vh-var(--component-feedback-dialog-container-spacing-margin)*2)] flex-col"
                  >
                     <div
                        data-testid={dialogBodyOverflowHidden ? 'dialog-overflow-hidden' : 'dialog-overflow'}
                        className={classNames('gap-component-feedback-dialog-body-spacing-gap flex w-full flex-1 min-h-0 flex-col')}
                     >
                        <div className="flex items-center justify-between">
                           <div className="gap-component-feedback-dialog-header-spacing-gap flex items-center">
                              {icon && (
                                 <div data-testid="dialog-icon" className="text-component-feedback-dialog-header-start-color-fill">
                                    <Icon icon={icon} />
                                 </div>
                              )}
                              {title && (
                                 <div data-testid="dialog-title" className="text-component-feedback-dialog-header-middle-color-text">
                                    <Typography variant="heading-4" color="strong" id={titleId}>
                                       {title}
                                    </Typography>
                                 </div>
                              )}
                           </div>
                           {closable && <IconButton icon="times" ariaLabel="close" onClick={handleClose} />}
                        </div>
                        <div
                           style={{ overflowX: contentOverflowX, overflowY: contentOverflowY }}
                           className="text-component-feedback-dialog-body-color-text min-h-0 flex-1"
                        >
                           {children}
                        </div>
                     </div>
                     {actions && (
                        <div
                           data-testid="dialog-actions"
                           className={classNames(
                              'gap-component-feedback-dialog-actions-spacing-gap mt-auto flex w-full shrink-0',
                              actionsLayout === 'vertical' ? 'flex-col [&>*]:w-full' : 'phone:flex-col justify-end'
                           )}
                        >
                           {actions}
                        </div>
                     )}
                  </div>
               </div>
            </FocusTrap>
         </CSSTransition>
      </ClientPortal>
   );
};
